import { Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { MarkdownField, MarkdownInput } from '@react-admin/ra-markdown';
import { useCallback, useMemo, useState } from 'react';
import {
  // Button without permissions
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  Button,
  RecordContextProvider,
  required,
  SelectInput,
  useTranslate,
} from 'react-admin';

import { PostContactRequestEndpoint } from '@Common/types/bo_external/contact_request';

import {
  CreateInDialogButton,
  DialogTitleWithX,
  ShowSection,
  StepperForm,
  StepperFormStep,
} from '@sh4res/bo-common/components';
import { B2bContractSelectInput } from '@sh4res/bo-common/fields';
import { useWatchObject } from '@sh4res/bo-common/hooks';

import { TranslationKeys } from '@I18n/types';
import { contactFormSubjectChoices, SubjectChoiceKeys } from '@Resources/contact_request/choices';
import { logAction } from '@Utils/analytics';

const RequestStep = () => {
  const testId = 'BoExternalRequestStep';
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab' && !event.shiftKey) {
      const target = event.target as HTMLElement;

      if (target.closest('.toastui-editor')) {
        event.preventDefault();
        const nextButton = document.getElementById('next_button');
        nextButton?.focus();
      }
    }
  };

  return (
    // It is not an interactive UI element, so no ARIA role is needed.
    // oxlint-disable-next-line jsx-a11y/no-static-element-interactions
    <div onKeyDown={handleKeyDown}>
      <ShowSection
        leftAligned
        label={'contact_support_page.contact_request.sections.request'}
        noCard={true}>
        <SelectInput
          choices={contactFormSubjectChoices}
          source="subject"
          validate={required()}
          data-testid={`${testId}_SubjectSelect`}
        />

        <B2bContractSelectInput
          reference="contracts"
          source="contract_id"
          filter={{}}
          validate={required()}
          testId={`${testId}_ContractSelect`}
        />
      </ShowSection>

      <ShowSection
        leftAligned
        label={'contact_support_page.contact_request.sections.request_details'}
        noCard={true}>
        <MarkdownInput
          source="details"
          validate={required()}
          data-testid={`${testId}_DetailsInput`}
          toolbarItems={[
            [
              ['heading', 'bold', 'italic', 'strike'],
              ['link'],
              ['ul', 'ol', 'task'],
              ['hr', 'quote'],
            ].flat(),
          ]}
          hideModeSwitch={true}
        />
      </ShowSection>
    </div>
  );
};

const ConfirmationStep = () => {
  const testId = 'BoExternalConfirmationStep';
  const translate = useTranslate();
  const {
    subject,
    contract_id: contractId,
    details,
  } = useWatchObject<PostContactRequestEndpoint['Client']['RequestBody']>()([
    'subject',
    'contract_id',
    'details',
  ]);
  const subjectTranslation = useMemo(
    () => `contact_support_page.contact_request.choices.subject.${subject}` as const,
    [subject],
  );

  return (
    <>
      <RecordContextProvider value={{ details, subject }}>
        <ShowSection
          leftAligned
          label={'contact_support_page.contact_request.sections.request'}
          noCard={true}>
          <Typography>
            {translate('contact_support_page.contact_request.fields.subject')}:{' '}
            {translate(subjectTranslation as TranslationKeys)}
          </Typography>

          <Typography>
            {translate('contact_support_page.contact_request.fields.contract_id')}: {contractId}
          </Typography>
        </ShowSection>

        <ShowSection
          leftAligned
          label={'contact_support_page.contact_request.sections.request_details'}
          noCard={true}>
          <MarkdownField
            source="details"
            extendedAutolinks={true}
            label={false}
            data-testid={`${testId}_DetailsField`}
            sx={{
              display: 'grid',
              border: '1px solid #dadde6',
              borderRadius: '4px',
              padding: '20px',
              '& .toastui-editor-contents': {
                overflowWrap: 'anywhere !important',
                '& p': { margin: 0 },
              },
            }}
          />
        </ShowSection>
      </RecordContextProvider>
    </>
  );
};

export function ContactRequestShow() {
  const confirmationDialog = useCampaignConfirmationDialog();
  const translate = useTranslate();

  const steps: StepperFormStep[] = [
    {
      label: 'contact_support_page.contact_request.steps.request',
      content: <RequestStep />,
    },
    {
      label: 'contact_support_page.contact_request.steps.confirmation',
      content: <ConfirmationStep />,
    },
  ];

  const onSuccess = useCallback(() => {
    logAction('Contact request submitted');
    confirmationDialog.open();
  }, [confirmationDialog]);

  const handleTransform = useCallback(
    (data: PostContactRequestEndpoint['Client']['RequestBody']) => {
      const subjectTranslationKey =
        `contact_support_page.contact_request.choices.subject.${data.subject as SubjectChoiceKeys}` as const;
      return {
        ...data,
        subject: translate(subjectTranslationKey),
      };
    },
    [translate],
  );

  return (
    <>
      <CreateInDialogButton
        data-testid={'BoExternalContactRequestShow_CreateContactButton'}
        resource="contact_request"
        fullWidth
        maxWidth="lg"
        mutationOptions={{ onSuccess }}
        transform={handleTransform}
        ButtonProps={{ variant: 'contained', color: 'primary', startIcon: null }}
        label={'contact_support_page.contact_request.label'}
        title={'contact_support_page.contact_request.title'}>
        <StepperForm
          steps={steps}
          permissions={{ resource: 'contact_request', action: 'show' }}
          testId="BoExternalContactRequestShow"
        />
      </CreateInDialogButton>
      {confirmationDialog.Dialog}
    </>
  );
}

const useCampaignConfirmationDialog = () => {
  const [isOpen, setOpen] = useState<boolean>(false);
  const translate = useTranslate();

  const open = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const close = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return {
    open,
    Dialog: (
      <Dialog open={isOpen} onClose={close} maxWidth="sm" fullWidth>
        <>
          <DialogTitleWithX onClose={close}>
            {translate('contact_support_page.contact_request.confirmation.title')}
          </DialogTitleWithX>

          <DialogContent>
            <Typography variant="body1">
              {translate('contact_support_page.contact_request.confirmation.message')}
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              data-testid={'BoExternalCampaignConfirmationDialog_ConfirmButton'}
              variant="contained"
              onClick={close}
              label="contact_support_page.contact_request.confirmation.confirmation"
            />
          </DialogActions>
        </>
      </Dialog>
    ),
  };
};
